import { diagnostics } from '@/diagnostics/browserDiagnostics';
import { useEffect, useRef, useState } from 'react';
import {
  Callout,
  Classes,
  Dialog,
  DialogBody,
  DialogFooter,
  InputGroup,
  HTMLSelect,
  ProgressBar,
} from '@blueprintjs/core';
import { HintButton as Button } from '@/components/base/HintButton';
import { useAppModel } from '@/models/AppModel';
import { MAX_SHARE_BYTES } from './format';
import type { LocalShare, ShareExpiry } from './client';
import { useProjectSharing } from './services';

export function ProjectShareDialog({
  name,
  source = 'saved',
  onClose,
  onManageShares,
}: {
  name: string;
  source?: 'saved' | 'current';
  onClose: () => void;
  onManageShares?: () => void;
}) {
  const model = useAppModel();
  const sourceContext = useRef(model.context);
  const services = useProjectSharing();
  const { client, history, link, copyLink, assertPrivacy, decode } = services;
  const [previousUnconfirmed] = useState(() =>
    history
      .read()
      .some((entry) => entry.title === name && entry.share?.state !== 'ready'),
  );
  const [permanentAvailable, setPermanentAvailable] = useState(false);
  const [expiry, setExpiry] = useState<ShareExpiry>('14-days');
  const [blob, setBlob] = useState<Blob>();
  const [phase, setPhase] = useState(
    source === 'current' ? 'Preparing current snapshot…' : 'Preparing saved snapshot…',
  );
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState<LocalShare>();
  const [copied, setCopied] = useState(false);
  const snapshotIdentity = useRef<string | undefined>(undefined);
  const item = useRef<LocalShare | undefined>(undefined);
  const operation = useRef<AbortController | undefined>(undefined);
  const locked = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    setPermanentAvailable(false);
    void client
      .capabilities(controller.signal)
      .then((capabilities) => {
        if (!controller.signal.aborted)
          setPermanentAvailable(capabilities.permanent === true);
      })
      .catch(() => {
        /* Capability failures leave ordinary anonymous sharing available. */
      });
    return () => controller.abort();
  }, [client]);
  const assertCurrentSource = () => {
    if (source !== 'current') return;
    const context = model.context;
    if (
      !context?.project ||
      context !== sourceContext.current ||
      context.project.name !== name
    )
      throw new Error(
        'The editor project changed. Close this dialog and share the project again.',
      );
    if (context.challenge || context.challengeAuthoring)
      throw new Error('Challenge editors cannot share public project links.');
  };
  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        assertCurrentSource();
        const file =
          source === 'current'
            ? await model.exportCurrentProjectFile(true)
            : await model.exportSavedProjectFile(name);
        assertCurrentSource();
        if (!file) throw new Error('This saved project is no longer available.');
        if (file.size > MAX_SHARE_BYTES)
          throw new Error(
            'This project exceeds 15 MB (15,000,000 bytes). Local exports remain available.',
          );
        const snapshot = await decode(file, controller.signal);
        assertCurrentSource();
        snapshotIdentity.current = snapshot.mainChipId;
        if (!controller.signal.aborted) {
          setBlob(file);
          setPhase('Ready to share');
        }
      } catch (cause) {
        if (!controller.signal.aborted) {
          diagnostics.record('publish');
          setError(
            cause instanceof Error ? cause.message : 'Unable to prepare this project.',
          );
          setPhase('Preparation failed');
        }
      }
    })();
    return () => {
      controller.abort();
      operation.current?.abort();
    };
  }, [model, name, source, decode]);
  const share = async () => {
    if (!blob || locked.current || previousUnconfirmed) return;
    locked.current = true;
    const controller = new AbortController();
    operation.current = controller;
    setBusy(true);
    setError('');
    setProgress(0);
    setPhase('Preparing upload…');
    const beforeTransfer = async () => {
      await assertPrivacy(name, snapshotIdentity.current ?? '', controller.signal);
      assertCurrentSource();
    };
    try {
      assertCurrentSource();
      await assertPrivacy(name, snapshotIdentity.current ?? '', controller.signal);
      assertCurrentSource();
      item.current ??= history.prepare(name, blob.size, expiry);
      let metadata = await client.reserve(
        item.current,
        controller.signal,
        beforeTransfer,
      );
      if (metadata.state !== 'ready') {
        assertCurrentSource();
        await assertPrivacy(name, snapshotIdentity.current ?? '', controller.signal);
        assertCurrentSource();
        setPhase('Uploading…');
        metadata = await client.upload(
          item.current,
          blob,
          (fraction) => {
            setProgress(fraction);
            if (fraction === 1) setPhase('Validating snapshot…');
          },
          controller.signal,
          beforeTransfer,
        );
      }
      if (!controller.signal.aborted) {
        setReady({ ...item.current, share: metadata });
        setPhase('Link ready');
      }
    } catch (cause) {
      if (controller.signal.aborted)
        setPhase('Upload cancelled. Retry checks whether it completed.');
      else {
        diagnostics.record('publish');
        setError(cause instanceof Error ? cause.message : 'Unable to share.');
        setPhase('Share not confirmed');
      }
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  return (
    <Dialog
      isOpen
      title="Share project"
      onClose={onClose}
      className={Classes.DARK}
      style={{ width: 460, maxWidth: 'calc(100vw - 24px)' }}
    >
      <DialogBody>
        <h3>{name}</h3>
        {previousUnconfirmed && (
          <Callout intent="warning">
            <p>
              A previous share of this project is unconfirmed. Check its status or revoke
              it before creating another snapshot.
            </p>
            <Button onClick={onManageShares ?? onClose}>Review previous share</Button>
          </Callout>
        )}
        <p>
          {source === 'current'
            ? 'Share a snapshot of this editor captured when this dialog opened, including unsaved project changes. Unsaved chip drafts are included as separate editable draft chips; they do not replace saved chip definitions or placed instances. Your local project is not saved or changed.'
            : 'Share the saved snapshot. Unsaved editor changes are not included.'}
        </p>
        <p>
          Anyone with this link can open and copy this project. Opening a link never
          requires an account.
        </p>
        {permanentAvailable ? (
          <div>
            <label htmlFor="project-share-expiry">Link expiration</label>{' '}
            <HTMLSelect
              id="project-share-expiry"
              value={expiry}
              disabled={busy || !!item.current}
              onChange={(event) => {
                if (!busy && !item.current) setExpiry(event.target.value as ShareExpiry);
              }}
              options={[
                { label: '14 days', value: '14-days' },
                { label: 'No expiration (admin)', value: 'permanent' },
              ]}
            />
          </div>
        ) : (
          <p>Create a link without an account. Links expire after 14 days.</p>
        )}
        {expiry === 'permanent' && (
          <p>
            This snapshot will have no expiration. Administrator access is checked when
            creating it.
          </p>
        )}
        {blob && <p>{blob.size.toLocaleString()} bytes · 15 MB maximum</p>}
        <p role="status" aria-live="polite">
          {phase}
        </p>
        {busy && <ProgressBar value={progress} aria-label="Upload progress" />}
        {error && (
          <Callout intent="danger" role="alert">
            {error}
          </Callout>
        )}
        {ready?.share && (
          <>
            <label htmlFor="project-share-link">Share link</label>
            <InputGroup
              id="project-share-link"
              readOnly
              value={link(ready.share.id)}
              onFocus={(event) => event.target.select()}
            />
            <p>
              {ready.share.expiresAt === null
                ? 'No expiration'
                : `Expires ${new Date(ready.share.expiresAt).toUTCString()}`}
            </p>
            <Button
              icon="clipboard"
              onClick={() => {
                void copyLink(ready.share!.id)
                  .then(() => setCopied(true))
                  .catch(() => setError('Copy failed. Select and copy the link above.'));
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </>
        )}
        <p>
          Your browser keeps the ability to revoke this link. Clearing browser data loses
          it. Downloaded copies cannot be recalled.
        </p>
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button
              onClick={() => {
                operation.current?.abort();
                onClose();
              }}
            >
              {busy ? 'Cancel' : 'Close'}
            </Button>
            {!ready && (
              <Button
                intent="primary"
                disabled={!blob || busy || previousUnconfirmed}
                onClick={() => void share()}
              >
                {item.current ? 'Retry share' : 'Create share link'}
              </Button>
            )}
          </>
        }
      />
    </Dialog>
  );
}
